// pages/Documents/Documents.js - FIXED WITH EMAIL NOTIFICATION
import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Card,
  Form,
  Image,
} from "react-bootstrap";
import {
  FaFileAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaStore,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaUsers,
  FaLink,
  FaCopy,
  FaFileUpload,
  FaExclamationTriangle,
  FaSync,
  FaIdCard,
  FaFileInvoice,
  FaBuilding,
  FaUniversity,
  FaDownload,
  FaEyeSlash,
} from "react-icons/fa";
import axios from "axios";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import "./Documents.css";

const SELLER_API_URL = "https://api.brandelvendor.starlighttechlabsindia.com/api/seller";
const API_BASE = "https://api.brandelvendor.starlighttechlabsindia.com";

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads')) return `${API_BASE}${imagePath}`;
  return `${API_BASE}${imagePath}`;
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [syncing, setSyncing] = useState(false);

  const [showDetail, setShowDetail] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem("token");

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${SELLER_API_URL}/admin/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setDocuments(res.data.documents || []);
        setFilteredDocs(res.data.documents || []);
      } else {
        setError("Failed to fetch documents");
      }
    } catch (err) {
      console.error("Fetch documents error:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError(err.response?.data?.message || "Failed to fetch documents");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    let filtered = documents;

    if (statusFilter !== "all") {
      filtered = filtered.filter((doc) => doc.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.email?.toLowerCase().includes(term) ||
          doc.company?.toLowerCase().includes(term) ||
          doc.trackingId?.toLowerCase().includes(term)
      );
    }

    setFilteredDocs(filtered);
  }, [documents, statusFilter, searchTerm]);

  // ================= VERIFY DOCUMENT WITH EMAIL =================
  const handleVerify = async () => {
    if (!selectedDoc) return;

    setActionLoading(true);
    try {
      // 1. Update document status to verified
      const verifyRes = await axios.patch(
        `${SELLER_API_URL}/admin/documents/verify/${selectedDoc._id}`,
        { status: "verified" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (verifyRes.data.success) {
        // 2. Send approval email to seller
        try {
          const emailRes = await axios.post(
            `${SELLER_API_URL}/send-approval-email`,
            {
              email: selectedDoc.email,
              name: selectedDoc.company || "Seller",
              company: selectedDoc.company,
              vendorId: selectedDoc.sellerId || "Vendor ID will be sent separately"
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (emailRes.data.success) {
            setSuccess(`✅ Documents for ${selectedDoc.email} verified! Approval email sent.`);
          } else {
            setSuccess(`✅ Documents for ${selectedDoc.email} verified! (Email failed: ${emailRes.data.message || 'Unknown error'})`);
          }
        } catch (emailErr) {
          console.error("Email error:", emailErr.response?.data || emailErr.message);
          setSuccess(`✅ Documents for ${selectedDoc.email} verified! (Email notification failed - check email configuration)`);
        }

        setShowVerifyModal(false);
        fetchDocuments();
      }
    } catch (err) {
      console.error("Verify error:", err);
      setError(err.response?.data?.message || "Failed to verify documents");
    } finally {
      setActionLoading(false);
    }
  };

  // ================= REJECT DOCUMENT WITH EMAIL =================
  const handleReject = async () => {
    if (!selectedDoc) return;

    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      // 1. Update document status to rejected
      const rejectRes = await axios.patch(
        `${SELLER_API_URL}/admin/documents/verify/${selectedDoc._id}`,
        { status: "rejected", rejectionReason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (rejectRes.data.success) {
        // 2. Send rejection email to seller
        try {
          const emailRes = await axios.post(
            `${SELLER_API_URL}/send-rejection-email`,
            {
              email: selectedDoc.email,
              name: selectedDoc.company || "Seller",
              company: selectedDoc.company,
              reason: rejectionReason
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (emailRes.data.success) {
            setSuccess(`✅ Documents for ${selectedDoc.email} rejected! Rejection email sent.`);
          } else {
            setSuccess(`✅ Documents for ${selectedDoc.email} rejected! (Email failed: ${emailRes.data.message || 'Unknown error'})`);
          }
        } catch (emailErr) {
          console.error("Email error:", emailErr.response?.data || emailErr.message);
          setSuccess(`✅ Documents for ${selectedDoc.email} rejected! (Email notification failed - check email configuration)`);
        }

        setShowRejectModal(false);
        setRejectionReason("");
        fetchDocuments();
      }
    } catch (err) {
      console.error("Reject error:", err);
      setError(err.response?.data?.message || "Failed to reject documents");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "verified":
        return (
          <Badge bg="success" className="px-3 py-2">
            <FaCheckCircle className="me-1" /> Verified
          </Badge>
        );
      case "submitted":
        return (
          <Badge bg="warning" className="px-3 py-2">
            <FaClock className="me-1" /> Pending
          </Badge>
        );
      case "rejected":
        return (
          <Badge bg="danger" className="px-3 py-2">
            <FaTimesCircle className="me-1" /> Rejected
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2">
            <FaClock className="me-1" /> Draft
          </Badge>
        );
    }
  };

  const getDocumentFields = (doc) => {
    const fields = [];
    
    if (doc.aadhaar?.number) {
      fields.push({ label: "Aadhaar Number", value: doc.aadhaar.number, verified: doc.aadhaar.verified });
    }
    if (doc.pan?.number) {
      fields.push({ label: "PAN Number", value: doc.pan.number, verified: doc.pan.verified });
    }
    if (doc.gst?.number) {
      fields.push({ label: "GST Number", value: doc.gst.number, verified: doc.gst.verified });
    }
    if (doc.bank?.accountNumber) {
      fields.push({ label: "Bank Account", value: `****${doc.bank.accountNumber.slice(-4)}`, verified: doc.bank.verified });
    }
    if (doc.contact?.phone) {
      fields.push({ label: "Phone", value: doc.contact.phone, verified: false });
    }
    
    return fields;
  };

  const getStats = () => {
    const total = documents.length;
    const draft = documents.filter((d) => d.status === "draft").length;
    const submitted = documents.filter((d) => d.status === "submitted").length;
    const verified = documents.filter((d) => d.status === "verified").length;
    const rejected = documents.filter((d) => d.status === "rejected").length;
    return { total, draft, submitted, verified, rejected };
  };

  const stats = getStats();

  if (loading) {
    return (
      <>
        <Header />
        <Sidebar />
        <main className="admin-content mt-5">
          <Container>
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading documents...</p>
            </div>
          </Container>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Sidebar />
      <main className="admin-content mt-5">
        <Container fluid>
          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              <FaExclamationTriangle className="me-2" />
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" onClose={() => setSuccess("")} dismissible>
              {success}
            </Alert>
          )}

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4>
              <FaFileAlt className="me-2" />
              Seller Documents
              <Badge bg="secondary" className="ms-2">
                {filteredDocs.length}
              </Badge>
            </h4>
            <Button
              variant="outline-info"
              size="sm"
              onClick={fetchDocuments}
              disabled={loading}
            >
              <FaSync className={loading ? "fa-spin" : ""} /> 
              {loading ? "Loading..." : "Refresh"}
            </Button>
          </div>

          {/* Stats Cards */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <h6 className="text-muted">Total</h6>
                  <h3>{stats.total}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm border-secondary">
                <Card.Body>
                  <h6 className="text-muted">Draft</h6>
                  <h3 className="text-secondary">{stats.draft}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm border-warning">
                <Card.Body>
                  <h6 className="text-muted">Pending</h6>
                  <h3 className="text-warning">{stats.submitted}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm border-success">
                <Card.Body>
                  <h6 className="text-muted">Verified</h6>
                  <h3 className="text-success">{stats.verified}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filters */}
          <Row className="mb-4">
            <Col md={8}>
              <Form.Control
                type="text"
                placeholder="Search by email, company, or tracking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-100"
              />
            </Col>
            <Col md={4}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Col>
          </Row>

          {/* Table */}
          <div className="table-responsive">
            <Table bordered hover className="documents-table">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Tracking ID</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Documents</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <FaFileAlt size={40} className="mb-3" />
                      <p>No documents found</p>
                    </td>
                  </tr>
                ) : (
                  filteredDocs.map((doc, index) => {
                    const documentFields = getDocumentFields(doc);
                    const hasDocuments = documentFields.length > 0;
                    
                    return (
                      <tr key={doc._id}>
                        <td>{index + 1}</td>
                        <td>
                          <code className="fw-bold">{doc.trackingId}</code>
                        </td>
                        <td>{doc.email}</td>
                        <td>{doc.company}</td>
                        <td>
                          {hasDocuments ? (
                            <div className="d-flex flex-wrap gap-1">
                              {doc.aadhaar?.number && (
                                <Badge bg="primary" className="me-1">Aadhaar</Badge>
                              )}
                              {doc.pan?.number && (
                                <Badge bg="warning" className="me-1">PAN</Badge>
                              )}
                              {doc.gst?.number && (
                                <Badge bg="success" className="me-1">GST</Badge>
                              )}
                              {doc.bank?.accountNumber && (
                                <Badge bg="info" className="me-1">Bank</Badge>
                              )}
                              {doc.business?.registrationNumber && (
                                <Badge bg="dark" className="me-1">Business</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted">No documents uploaded</span>
                          )}
                        </td>
                        <td>{getStatusBadge(doc.status)}</td>
                        <td>
                          {doc.submissionDate
                            ? new Date(doc.submissionDate).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setShowDetail(true);
                            }}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          {doc.status === "submitted" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                className="me-1"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setShowVerifyModal(true);
                                }}
                                title="Verify"
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setShowRejectModal(true);
                                }}
                                title="Reject"
                              >
                                <FaTimes />
                              </Button>
                            </>
                          )}
                          {doc.status !== "submitted" && (
                            <Badge bg="secondary">Reviewed</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Container>
      </main>

      {/* ================= DETAIL MODAL ================= */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileAlt className="me-2" />
            Document Details
          </Modal.Title>
        </Modal.Header>
        {selectedDoc && (
          <Modal.Body>
            <Row>
              <Col md={6}>
                <div className="detail-item">
                  <label>Tracking ID</label>
                  <p><code className="fw-bold">{selectedDoc.trackingId}</code></p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p><FaEnvelope className="me-1 text-muted" /> {selectedDoc.email}</p>
                </div>
                <div className="detail-item">
                  <label>Company</label>
                  <p><FaStore className="me-1 text-primary" /> {selectedDoc.company}</p>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <p>{getStatusBadge(selectedDoc.status)}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="detail-item">
                  <label>Submitted On</label>
                  <p>
                    <FaCalendarAlt className="me-1 text-muted" />
                    {selectedDoc.submissionDate
                      ? new Date(selectedDoc.submissionDate).toLocaleString()
                      : "Not submitted yet"}
                  </p>
                </div>
                <div className="detail-item">
                  <label>Last Saved</label>
                  <p>
                    <FaClock className="me-1 text-muted" />
                    {new Date(selectedDoc.lastSaved).toLocaleString()}
                  </p>
                </div>
                {selectedDoc.rejectionReason && (
                  <div className="detail-item">
                    <label>Rejection Reason</label>
                    <p className="text-danger">{selectedDoc.rejectionReason}</p>
                  </div>
                )}
              </Col>
            </Row>

            <hr />
            <h6 className="mb-3">Uploaded Documents</h6>

            <Row>
              {/* Aadhaar */}
              {selectedDoc.aadhaar?.number && (
                <Col md={6}>
                  <Card className="mb-2 border-primary">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FaIdCard className="text-primary me-2" />
                          <strong>Aadhaar</strong>
                          <div className="small text-muted">
                            {selectedDoc.aadhaar.number}
                          </div>
                        </div>
                        <Badge bg={selectedDoc.aadhaar.verified ? "success" : "warning"}>
                          {selectedDoc.aadhaar.verified ? "Verified" : ""}
                        </Badge>
                      </div>
                      {selectedDoc.aadhaar.frontImage && (
                        <div className="mt-1">
                          <small>
                            <a href={getImageUrl(selectedDoc.aadhaar.frontImage)} target="_blank" rel="noopener noreferrer">
                              View Front
                            </a>
                          </small>
                        </div>
                      )}
                      {selectedDoc.aadhaar.backImage && (
                        <div className="mt-1">
                          <small>
                            <a href={getImageUrl(selectedDoc.aadhaar.backImage)} target="_blank" rel="noopener noreferrer">
                              View Back
                            </a>
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* PAN */}
              {selectedDoc.pan?.number && (
                <Col md={6}>
                  <Card className="mb-2 border-warning">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FaFileInvoice className="text-warning me-2" />
                          <strong>PAN</strong>
                          <div className="small text-muted">
                            {selectedDoc.pan.number}
                          </div>
                        </div>
                        <Badge bg={selectedDoc.pan.verified ? "success" : "warning"}>
                          {selectedDoc.pan.verified ? "Verified" : ""}
                        </Badge>
                      </div>
                      {selectedDoc.pan.image && (
                        <div className="mt-1">
                          <small>
                            <a href={getImageUrl(selectedDoc.pan.image)} target="_blank" rel="noopener noreferrer">
                              View Document
                            </a>
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* GST */}
              {selectedDoc.gst?.number && (
                <Col md={6}>
                  <Card className="mb-2 border-success">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FaBuilding className="text-success me-2" />
                          <strong>GST</strong>
                          <div className="small text-muted">
                            {selectedDoc.gst.number}
                          </div>
                        </div>
                        <Badge bg={selectedDoc.gst.verified ? "success" : "warning"}>
                          {selectedDoc.gst.verified ? "Verified" : ""}
                        </Badge>
                      </div>
                      {selectedDoc.gst.certificate && (
                        <div className="mt-1">
                          <small>
                            <a href={getImageUrl(selectedDoc.gst.certificate)} target="_blank" rel="noopener noreferrer">
                              View Certificate
                            </a>
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Bank Account */}
              {selectedDoc.bank?.accountNumber && (
                <Col md={6}>
                  <Card className="mb-2 border-info">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FaUniversity className="text-info me-2" />
                          <strong>Bank Account</strong>
                          <div className="small text-muted">
                            {selectedDoc.bank.accountHolderName}
                          </div>
                          <div className="small text-muted">
                            ****{selectedDoc.bank.accountNumber.slice(-4)}
                          </div>
                          <div className="small text-muted">
                            IFSC: {selectedDoc.bank.ifscCode}
                          </div>
                        </div>
                        <Badge bg={selectedDoc.bank.verified ? "success" : "warning"}>
                          {selectedDoc.bank.verified ? "Verified" : ""}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Business Registration */}
              {selectedDoc.business?.registrationNumber && (
                <Col md={6}>
                  <Card className="mb-2 border-dark">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FaBuilding className="text-dark me-2" />
                          <strong>Business Registration</strong>
                          <div className="small text-muted">
                            {selectedDoc.business.registrationNumber}
                          </div>
                          <div className="small text-muted">
                            {selectedDoc.business.registrationType}
                          </div>
                        </div>
                        <Badge bg={selectedDoc.business.verified ? "success" : "warning"}>
                          {selectedDoc.business.verified ? "Verified" : ""}
                        </Badge>
                      </div>
                      {selectedDoc.business.certificate && (
                        <div className="mt-1">
                          <small>
                            <a href={getImageUrl(selectedDoc.business.certificate)} target="_blank" rel="noopener noreferrer">
                              View Certificate
                            </a>
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Contact Information */}
              {selectedDoc.contact?.phone && (
                <Col md={6}>
                  <Card className="mb-2 border-secondary">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <FaPhone className="text-secondary me-2" />
                          <strong>Contact</strong>
                          <div className="small text-muted">
                            Phone: {selectedDoc.contact.phone}
                          </div>
                          {selectedDoc.contact.alternatePhone && (
                            <div className="small text-muted">
                              Alternate: {selectedDoc.contact.alternatePhone}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ================= VERIFY MODAL ================= */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCheckCircle className="me-2 text-success" />
            Verify Documents
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDoc && (
            <>
              <p><strong>Email:</strong> {selectedDoc.email}</p>
              <p><strong>Company:</strong> {selectedDoc.company}</p>
              <p><strong>Tracking ID:</strong> <code>{selectedDoc.trackingId}</code></p>
              <Alert variant="success" className="mt-3">
                <strong>This will:</strong>
                <ul className="mb-0 mt-1">
                  <li>✅ Approve all documents</li>
                  <li>✅ Mark the seller as verified</li>
                  <li>✅ Update document status to "Verified"</li>
                  <li>📧 Send approval email to <strong>{selectedDoc.email}</strong></li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleVerify} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaCheck className="me-2" />}
            Verify & Send Email
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= REJECT MODAL ================= */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTimesCircle className="me-2 text-danger" />
            Reject Documents
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDoc && (
            <>
              <p><strong>Email:</strong> {selectedDoc.email}</p>
              <p><strong>Company:</strong> {selectedDoc.company}</p>
              <Form.Group className="mt-3">
                <Form.Label>Rejection Reason <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Please provide a reason for rejection..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                />
              </Form.Group>
              <Alert variant="danger" className="mt-3">
                <strong>This will:</strong>
                <ul className="mb-0 mt-1">
                  <li>❌ Reject all documents</li>
                  <li>📧 Send rejection email to <strong>{selectedDoc.email}</strong></li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaTimes className="me-2" />}
            Reject & Send Email
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Documents;